import { Health } from '@capgo/capacitor-health';
import { formatLocalDate } from './date';

// Minimización de datos (RGPD Art. 5.1.c y Apple Guideline 5.1.1):
// Solo se solicitan métricas empleadas en el motor de predisposición fisiológica y composición corporal deportiva.
const METRICS = [
  'sleep',
  'steps',
  'restingHeartRate',
  'heartRateVariability',
  'weight',
  'respiratoryRate',
  'oxygenSaturation',
  'vo2Max',
  'bodyFat',
  'bodyTemperature',
  'calories'
];

export async function requestHealthPermissions() {
  try {
    const isAvailable = await Health.isAvailable();
    if (!isAvailable) return false;
    await Health.requestAuthorization({ read: METRICS });
    return true;
  } catch (error) {
    return false;
  }
}

const OFFLINE_HEALTH_QUEUE_KEY = 'nousmetrics_pending_health_sync_v1';

export function enqueueOfflineHealthPayload(payload) {
  try {
    const raw = localStorage.getItem(OFFLINE_HEALTH_QUEUE_KEY);
    const queue = raw ? JSON.parse(raw) : [];
    queue.push({ payload, queuedAt: new Date().toISOString() });
    // Mantener máximo 10 lotes en cola
    const trimmed = queue.slice(-10);
    localStorage.setItem(OFFLINE_HEALTH_QUEUE_KEY, JSON.stringify(trimmed));
  } catch (_) {
    // ignore storage quota errors
  }
}

export async function flushOfflineHealthQueue(backendUrl, token) {
  if (!backendUrl || !token) return 0;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;
  try {
    const raw = localStorage.getItem(OFFLINE_HEALTH_QUEUE_KEY);
    if (!raw) return 0;
    const queue = JSON.parse(raw);
    if (!Array.isArray(queue) || queue.length === 0) return 0;

    const remaining = [];
    let flushedCount = 0;
    for (const item of queue) {
      try {
        const res = await fetch(`${backendUrl}/health/sync-native`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(item.payload)
        });
        if (res.ok) {
          flushedCount++;
        } else {
          remaining.push(item);
        }
      } catch (_) {
        remaining.push(item);
      }
    }

    if (remaining.length === 0) {
      localStorage.removeItem(OFFLINE_HEALTH_QUEUE_KEY);
    } else {
      localStorage.setItem(OFFLINE_HEALTH_QUEUE_KEY, JSON.stringify(remaining));
    }
    return flushedCount;
  } catch (_) {
    return 0;
  }
}

export async function syncHealthData(backendUrl, token) {
  try {
    await flushOfflineHealthQueue(backendUrl, token);
    const isAvailable = await Health.isAvailable();
    if (!isAvailable) return;

    // Trigger permission prompt for any new metrics added to METRICS array
    await requestHealthPermissions();

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30); // 30 días para garantizar pasillo de recuperación de 28d completo
    startDate.setHours(0, 0, 0, 0);

    const metricsByDate = {};

    const formatDate = (dateStr) => {
      return formatLocalDate(dateStr);
    };

    const addToDate = (date, key, val) => {
      const d = formatDate(date);
      if (!metricsByDate[d]) metricsByDate[d] = { date: d };
      
      if (key === 'sleepDuration' || key === 'steps' || key === 'activeCalories') {
        metricsByDate[d][key] = (metricsByDate[d][key] || 0) + val;
      } else if (key === 'hrv' || key === 'spO2' || key === 'respiratoryRate' || key === 'temperature' || key === 'glucoseCgm') {
        const sampleKey = `${key}Samples`;
        if (!metricsByDate[d][sampleKey]) metricsByDate[d][sampleKey] = [];
        metricsByDate[d][sampleKey].push(val);
      } else {
        metricsByDate[d][key] = val;
      }
    };

    function mergeIntervals(intervals) {
      if (!intervals || intervals.length === 0) return [];
      const sorted = intervals
        .map(i => ({
          start: new Date(i.startDate).getTime(),
          end: new Date(i.endDate).getTime()
        }))
        .filter(i => !isNaN(i.start) && !isNaN(i.end) && i.end > i.start)
        .sort((a, b) => a.start - b.start);

      if (sorted.length === 0) return [];
      const merged = [{ start: sorted[0].start, end: sorted[0].end }];
      for (let j = 1; j < sorted.length; j++) {
        const curr = sorted[j];
        const prev = merged[merged.length - 1];
        if (curr.start <= prev.end) {
          prev.end = Math.max(prev.end, curr.end);
        } else {
          merged.push({ start: curr.start, end: curr.end });
        }
      }
      return merged;
    }

    function intervalHours(merged) {
      return merged.reduce((acc, i) => acc + (i.end - i.start) / (1000 * 60 * 60), 0);
    }

    try {
      const sleepData = await Health.readSamples({ 
        dataType: 'sleep', 
        startDate: startDate.toISOString(), 
        endDate: endDate.toISOString(),
        limit: 10000 
      });

      const rawSamplesByDate = {};
      
      sleepData.samples.forEach(s => {
        // En iOS / Capacitor Health, las fases de sueño vienen dentro de s.stages
        const stagesList = (s.stages && Array.isArray(s.stages) && s.stages.length > 0)
          ? s.stages
          : [s];

        stagesList.forEach(item => {
          const start = item.startDate || s.startDate;
          const end = item.endDate || s.endDate;
          if (!start || !end) return;

          // Añadimos 12 horas para que el sueño de antes de medianoche (ej. 23:00) cuente para la mañana siguiente.
          const sleepDate = new Date(new Date(start).getTime() + 12 * 60 * 60 * 1000);
          const d = formatDate(sleepDate);
          
          if (!rawSamplesByDate[d]) {
            rawSamplesByDate[d] = {
              deep: [],
              light: [],
              rem: [],
              awake: [],
              genericAsleep: [],
              inBed: []
            };
          }
          
          const stageSample = { startDate: start, endDate: end };
          const state = String(item.stage || item.sleepState || item.value || s.stage || s.sleepState || s.value || '').toUpperCase();
          if (state.includes('DEEP') || state === 'ASLEEP_DEEP') {
            rawSamplesByDate[d].deep.push(stageSample);
          } else if (state.includes('REM') || state === 'ASLEEP_REM') {
            rawSamplesByDate[d].rem.push(stageSample);
          } else if (state.includes('LIGHT') || state.includes('CORE') || state === 'ASLEEP_CORE') {
            rawSamplesByDate[d].light.push(stageSample);
          } else if (state.includes('AWAKE')) {
            rawSamplesByDate[d].awake.push(stageSample);
          } else if (state.includes('INBED') || state.includes('IN_BED')) {
            rawSamplesByDate[d].inBed.push(stageSample);
          } else {
            rawSamplesByDate[d].genericAsleep.push(stageSample);
          }
        });
      });

      Object.keys(rawSamplesByDate).forEach(d => {
        const bucket = rawSamplesByDate[d];
        const mergedDeep = mergeIntervals(bucket.deep);
        const mergedLight = mergeIntervals(bucket.light);
        const mergedRem = mergeIntervals(bucket.rem);
        const mergedAwake = mergeIntervals(bucket.awake);
        const mergedInBed = mergeIntervals(bucket.inBed);

        const deepH = intervalHours(mergedDeep);
        const lightH = intervalHours(mergedLight);
        const remH = intervalHours(mergedRem);
        const awakeH = intervalHours(mergedAwake);
        const inBedH = intervalHours(mergedInBed);

        const hasStages = (deepH + lightH + remH) > 0;
        let actualSleepH;

        if (hasStages) {
          // Si el reloj registró fases detalladas, fusionamos todas las fases para garantizar cero solapamiento
          const allStageSamples = [...bucket.deep, ...bucket.light, ...bucket.rem];
          actualSleepH = intervalHours(mergeIntervals(allStageSamples));
        } else {
          // Si no hay fases (sólo muestras genéricas), fusionamos las muestras de sueño genérico
          actualSleepH = intervalHours(mergeIntervals(bucket.genericAsleep));
        }

        const finalSleepDuration = actualSleepH > 0 ? actualSleepH : (inBedH > awakeH ? (inBedH - awakeH) : inBedH);

        const efficiency = inBedH > 0 
          ? Math.min(100, Math.max(1, Math.round((finalSleepDuration / inBedH) * 100))) 
          : null;
        
        if (!metricsByDate[d]) metricsByDate[d] = { date: d };
        metricsByDate[d].sleepDuration = parseFloat(finalSleepDuration.toFixed(1));
        metricsByDate[d].sleepAwakeDuration = parseFloat(awakeH.toFixed(1));
        metricsByDate[d].sleepAwakeningsCount = mergedAwake.length;
        if (hasStages) {
          metricsByDate[d].deepSleepDuration = parseFloat(deepH.toFixed(1));
          metricsByDate[d].lightSleepDuration = parseFloat(lightH.toFixed(1));
          metricsByDate[d].remSleepDuration = parseFloat(remH.toFixed(1));
        }
        if (efficiency !== null && efficiency > 0 && efficiency <= 100) {
          metricsByDate[d].sleepEfficiency = efficiency;
        }

        // Puntuación de calidad de sueño (estándar clínico y alineado con Apple Health)
        let durScore;
        if (finalSleepDuration >= 7.5) durScore = 50;
        else if (finalSleepDuration >= 6.5) durScore = 42 + ((finalSleepDuration - 6.5) / 1.0) * 8;
        else if (finalSleepDuration >= 5.5) durScore = 30 + ((finalSleepDuration - 5.5) / 1.0) * 12;
        else durScore = Math.max(10, (finalSleepDuration / 5.5) * 30);

        const effVal = efficiency ? (efficiency / 100) : 0.95;
        let effScore = 30;
        if (effVal < 0.85) effScore = Math.max(10, 30 - Math.round((0.85 - effVal) * 60));

        let interScore = 20;
        const awCount = mergedAwake.length;
        if (awCount > 4) interScore = Math.max(5, 20 - Math.round((awCount - 4) * 0.9));
        if (awakeH > 0.75) interScore = Math.max(2, interScore - Math.round((awakeH - 0.75) * 10));

        let archMod = 0;
        if (hasStages && finalSleepDuration > 0) {
          const restRatio = (deepH + remH) / finalSleepDuration;
          if (restRatio >= 0.25) archMod = 3;
          else if (restRatio < 0.12 && finalSleepDuration >= 6) archMod = -4;
        }

        metricsByDate[d].sleepScore = Math.round(Math.max(10, Math.min(100, durScore + effScore + interScore + archMod)));
      });
    } catch (_) {
      // ignore
    }

    try {
      const rhrData = await Health.readSamples({ dataType: 'restingHeartRate', startDate: startDate.toISOString(), endDate: endDate.toISOString() });
      rhrData.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => addToDate(r.startDate, 'rhr', r.value));
    } catch (_) {
      // ignore
    }

    try {
      const hrvData = await Health.readSamples({ dataType: 'heartRateVariability', startDate: startDate.toISOString(), endDate: endDate.toISOString() });
      hrvData.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => addToDate(r.startDate, 'hrv', r.value));
    } catch (_) {
      // ignore
    }

    const longStartDate = new Date();
    longStartDate.setDate(longStartDate.getDate() - 730); // 2 años para capturar peso/grasa/vo2max históricos del atleta
    longStartDate.setHours(0, 0, 0, 0);

    try {
      console.log('SYNC: querying weight...');
      const weightData = await Health.readSamples({ dataType: 'weight', startDate: longStartDate.toISOString(), endDate: endDate.toISOString(), limit: 1000 });
      console.log('SYNC: weight queried', weightData);
      if (weightData && weightData.samples && weightData.samples.length > 0) {
        weightData.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => addToDate(r.startDate, 'weight', r.value));
        const latestWeightSample = weightData.samples[weightData.samples.length - 1];
        if (latestWeightSample && latestWeightSample.value) {
          const todayKey = formatDate(new Date());
          if (!metricsByDate[todayKey] || metricsByDate[todayKey].weight == null) {
            addToDate(new Date(), 'weight', latestWeightSample.value);
          }
        }
      }
    } catch(e) {
      console.error('SYNC ERROR: weight', e);
    }

    try {
      console.log('SYNC: querying steps...');
      let stepsSamples = [];
      try {
        const stepsData = await Health.queryAggregated({ dataType: 'steps', startDate: startDate.toISOString(), endDate: endDate.toISOString(), bucket: 'day', aggregation: 'sum' });
        if (stepsData && stepsData.samples && stepsData.samples.length > 0) {
          stepsSamples = stepsData.samples;
        }
      } catch (errQuery) {
        console.warn('SYNC: queryAggregated steps failed, falling back to readSamples', errQuery);
      }

      if (!stepsSamples || stepsSamples.length === 0) {
        const rawSteps = await Health.readSamples({ dataType: 'steps', startDate: startDate.toISOString(), endDate: endDate.toISOString(), limit: 10000 });
        if (rawSteps && rawSteps.samples && rawSteps.samples.length > 0) {
          const byDay = {};
          rawSteps.samples.forEach(s => {
            const d = formatDate(s.startDate);
            byDay[d] = (byDay[d] || 0) + (Number(s.value) || 0);
          });
          Object.entries(byDay).forEach(([dStr, sumVal]) => {
            stepsSamples.push({ startDate: dStr, value: Math.round(sumVal) });
          });
        }
      }
      console.log('SYNC: steps queried successfully, total samples:', stepsSamples.length);
      stepsSamples.forEach(r => addToDate(r.startDate, 'steps', r.value));
    } catch(e){
      console.error('SYNC ERROR: steps', e);
    }

    try {
      console.log('SYNC: querying activeCalories...');
      let calSamples = [];
      try {
        const calData = await Health.queryAggregated({ dataType: 'calories', startDate: startDate.toISOString(), endDate: endDate.toISOString(), bucket: 'day', aggregation: 'sum' });
        if (calData && calData.samples && calData.samples.length > 0) {
          calSamples = calData.samples;
        }
      } catch (e1) {
        console.warn('SYNC: queryAggregated calories failed, falling back to readSamples', e1);
      }

      if (!calSamples || calSamples.length === 0) {
        const rawCal = await Health.readSamples({ dataType: 'calories', startDate: startDate.toISOString(), endDate: endDate.toISOString(), limit: 10000 });
        if (rawCal && rawCal.samples && rawCal.samples.length > 0) {
          const byDay = {};
          rawCal.samples.forEach(s => {
            const d = formatDate(s.startDate);
            byDay[d] = (byDay[d] || 0) + (Number(s.value) || 0);
          });
          Object.entries(byDay).forEach(([dStr, sumVal]) => {
            calSamples.push({ startDate: dStr, value: Math.round(sumVal) });
          });
        }
      }
      console.log('SYNC: calories queried successfully, total samples:', calSamples.length);
      calSamples.forEach(r => addToDate(r.startDate, 'activeCalories', r.value));
    } catch(e){
      console.error('SYNC ERROR: activeCalories', e);
    }

    try {
      console.log('SYNC: querying respiratoryRate...');
      const respData = await Health.readSamples({ dataType: 'respiratoryRate', startDate: startDate.toISOString(), endDate: endDate.toISOString(), limit: 1000 });
      console.log('SYNC: respiratoryRate queried', respData);
      if (respData && respData.samples) {
        respData.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => addToDate(r.startDate, 'respiratoryRate', r.value));
      }
    } catch(e){
      console.error('SYNC ERROR: respiratoryRate', e);
    }

    try {
      console.log('SYNC: querying spO2...');
      const spO2Data = await Health.readSamples({ dataType: 'oxygenSaturation', startDate: longStartDate.toISOString(), endDate: endDate.toISOString(), limit: 1000 });
      console.log('SYNC: spO2 queried', spO2Data);
      if (spO2Data && spO2Data.samples) {
        spO2Data.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => {
          if (r.value != null) {
            const val = r.value <= 1 ? r.value * 100 : r.value;
            addToDate(r.startDate, 'spO2', val);
          }
        });
      }
    } catch(e){
      console.error('SYNC ERROR: spO2', e);
    }

    try {
      console.log('SYNC: querying vo2Max...');
      const vo2Data = await Health.readSamples({ dataType: 'vo2Max', startDate: longStartDate.toISOString(), endDate: endDate.toISOString(), limit: 500 });
      console.log('SYNC: vo2Max queried', vo2Data);
      if (vo2Data && vo2Data.samples && vo2Data.samples.length > 0) {
        vo2Data.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => addToDate(r.startDate, 'vo2Max', r.value));
        const latestVo2Sample = vo2Data.samples[vo2Data.samples.length - 1];
        if (latestVo2Sample && latestVo2Sample.value) {
          const todayKey = formatDate(new Date());
          if (!metricsByDate[todayKey] || metricsByDate[todayKey].vo2Max == null) {
            addToDate(new Date(), 'vo2Max', latestVo2Sample.value);
          }
        }
      }
    } catch(e){
      console.error('SYNC ERROR: vo2Max', e);
    }

    try {
      console.log('SYNC: querying bodyFat...');
      const fatData = await Health.readSamples({ dataType: 'bodyFat', startDate: longStartDate.toISOString(), endDate: endDate.toISOString(), limit: 500 });
      console.log('SYNC: bodyFat queried', fatData);
      if (fatData && fatData.samples && fatData.samples.length > 0) {
        fatData.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => {
          if (r.value != null) {
            const val = r.value <= 1 ? r.value * 100 : r.value;
            addToDate(r.startDate, 'bodyFat', val);
          }
        });
        const latestFatSample = fatData.samples[fatData.samples.length - 1];
        if (latestFatSample && latestFatSample.value != null) {
          const val = latestFatSample.value <= 1 ? latestFatSample.value * 100 : latestFatSample.value;
          const todayKey = formatDate(new Date());
          if (!metricsByDate[todayKey] || metricsByDate[todayKey].bodyFat == null) {
            addToDate(new Date(), 'bodyFat', val);
          }
        }
      }
    } catch(e){
      console.error('SYNC ERROR: bodyFat', e);
    }

    try {
      console.log('SYNC: querying bodyTemperature...');
      const tempData = await Health.readSamples({ dataType: 'bodyTemperature', startDate: startDate.toISOString(), endDate: endDate.toISOString() });
      console.log('SYNC: bodyTemperature queried', tempData);
      tempData.samples.sort((a,b) => new Date(a.startDate) - new Date(b.startDate)).forEach(r => addToDate(r.startDate, 'temperature', r.value));
    } catch(e) {
      console.error('SYNC ERROR: bodyTemperature', e);
    }

    console.log('SYNC: finished gathering metrics, parsing values...');

    Object.values(metricsByDate).forEach(m => {
      ['hrv', 'spO2', 'respiratoryRate', 'temperature', 'glucoseCgm'].forEach(k => {
        const sampleKey = `${k}Samples`;
        if (m[sampleKey] && m[sampleKey].length > 0) {
          const sum = m[sampleKey].reduce((a, b) => a + b, 0);
          const avg = sum / m[sampleKey].length;
          m[k] = (k === 'spO2' || k === 'hrv') ? parseFloat(avg.toFixed(1)) : parseFloat(avg.toFixed(2));
          delete m[sampleKey];
        }
      });
    });

    const metrics = Object.values(metricsByDate).map(m => {
      // Redondear a entero para evitar crasheos de Prisma en la versión antigua del backend en el VPS
      const rounded = { ...m };
      ['sleepScore', 'sleepAwakeningsCount', 'rhr', 'steps', 'activeCalories', 'systolic', 'diastolic'].forEach(key => {
        if (rounded[key] != null) {
          rounded[key] = Math.round(rounded[key]);
        }
      });
      return rounded;
    });

    if (metrics.length === 0) {
      console.log('No health data found to sync.');
      return [];
    }

    const payload = { startDate: startDate.toISOString(), endDate: endDate.toISOString(), metrics };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      enqueueOfflineHealthPayload(payload);
      return metrics;
    }

    try {
      const response = await fetch(`${backendUrl}/health/sync-native`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        enqueueOfflineHealthPayload(payload);
      } else {
        const data = await response.json();
        console.log('SYNC: backend response:', data);
      }
    } catch(e) {
      console.error('SYNC: backend push failed, queuing offline', e);
      enqueueOfflineHealthPayload(payload);
    }

    return metrics;
  } catch (error) {
    console.error('Error syncing health data:', error);
    return [];
  }
}
